import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import AgendaApi from 'api/agenda';
import AlertHelper from 'helpers/AlertHelper';
import withModalView from 'components/ui/withModalView';
import withContentLoader from 'components/ui/withContentLoader';
import withAuthorizer from 'components/ui/withAuthorizer';

import { Resources } from 'helpers/ResourceHelper';

import JobApi from 'api/job';

const AgendaEventDetailsPage = ({ data, backUrl }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const queryClient = useQueryClient();

  const mutationParams = {
    onSuccess: () => {
      queryClient.invalidateQueries('agenda');
    },
  };

  const { bot, input_data } = data;
  const { settings } = bot;
  const input_data_config = settings.find(item => item.id === 'input_data') || {};
  const { schema = {} } = input_data_config;
  const props = schema.properties || {};

  const renderPayload = () => {
    const data = [];

    for (const prop of Object.keys(props)) {
      const item = props[prop] || {};
      data.push(
        <tr key={prop}>
          <th>{item.title}:</th>
          <td>{getPayloadProp(prop, item)}</td>
        </tr>
      );
    }

    return data;
  };

  const getPayloadProp = (prop, item) => {
    const value = input_data[prop];
    if (['date', 'datetime'].includes(item.format)) {
      return formatDate(value);
    }
    return value || '--';
  };

  const formatDate = date => {
    if (!date) {
      return '--';
    }
    return moment(date).format(t('formats.datetime'));
  };

  const deleteMutation = useMutation(({ eventId }) => AgendaApi.delete(eventId), mutationParams);
  const deleteAllFromGroupMutation = useMutation(({ groupId }) => AgendaApi.deleteGroup(groupId), mutationParams);

  const handleRemoveEntry = async () => {
    const result = await AlertHelper.confirm({
      title: t('agenda.dialogs.remove_item.title'),
      html: t('agenda.dialogs.remove_item.description', { title: data.title, date: moment(data.start).format(t('formats.date')) }),
    });

    if (result.value) {
      deleteMutation.mutate({ eventId: data.id });
    }
    history.push(backUrl);
  };

  const handleRemoveEntryGroup = async () => {
    const result = await AlertHelper.confirmDanger({
      title: t('agenda.dialogs.remove_item.title'),
      html: t('agenda.dialogs.remove_item.description', { title: data.title, date: moment(data.start).format(t('formats.date')) }),
    });

    if (result.value) {
      deleteAllFromGroupMutation.mutate({ groupId: data.agenda_id });
    }
    history.push(backUrl);
  };

  return (
    <>
      <div className="container">
        <div className="mb-2">{t('bots.jobs.details.title')}</div>
        <h3>{data.bot.name}</h3>

        <table className="mt-4 table table-bordered border-primary">
          <tbody>
            <tr>
              <th>ID:</th>
              <td>{data.id}</td>
            </tr>

            <tr>
              <th>{t('columns.agenda_id')}:</th>
              <td>{data.agenda_id}</td>
            </tr>

            <tr>
              <th>{t('columns.title')}:</th>
              <td>{data.title}</td>
            </tr>

            {renderPayload()}

            <tr>
              <th>{t('columns.created_at')}:</th>
              <td>{formatDate(data.created_at || data.createdAt)}</td>
            </tr>

            <tr>
              <th>{t('columns.start_at')}:</th>
              <td>{formatDate(data.start_at)}</td>
            </tr>
          </tbody>
        </table>

        <div>
          <button type="button" onClick={handleRemoveEntry} className="btn btn-primary">
            {t('actions.remove_entry')}
          </button>

          <button type="button" onClick={handleRemoveEntryGroup} className="btn btn-outline-danger ms-3">
            {t('actions.remove_entry_group')}
          </button>
        </div>
      </div>
    </>
  );
};

const fetchData = async ({ routeMatch }) => {
  const { params } = routeMatch;
  return await JobApi.get(params.jobId);
};

const page = withContentLoader(AgendaEventDetailsPage, { name: 'job-details', fetchData });
export default withModalView(withAuthorizer(page, [Resources.BOT_MANAGER]));
